import { ReactNode } from 'react';

import { useTranslation } from 'react-i18next';

import { Skeleton } from '@/components/ui/skeleton';
import { FilterValues } from '@/hooks/use-filter-values';
import { ErrorState } from '@/modules/common/components/organisms/error-state';

import { getOverheadTypeIcon } from '../../constants/overhead-type-icon-map';
import { useOverheadsSummary } from '../../hooks/use-overheads-summary';
import { OverheadType } from '../../types/overhead';
import { OverheadsSummaryAnalysisCard } from './overheads-summary-analysis-card';
import { OverheadsSummaryCard } from './overheads-summary-card';
import { OverheadsSummaryCardSlot, OverheadsSummaryCardsRow } from './overheads-summary-cards-row';
import { OverheadsSummaryCompact } from './overheads-summary-compact';

interface OverheadsSummaryRenditions {
  /** Title + subtitle of the expanded rendition (fades out on collapse). */
  expandedHeader: ReactNode;
  /** The two summary cards of the expanded rendition (scales + fades on collapse). */
  expandedBody: ReactNode;
  /** Compact chip row with tooltips; intended for the collapsed slot. */
  collapsed: ReactNode;
}

/**
 * Skeleton of one summary card, mirroring the StatCard anatomy (icon + title
 * header, three label/value rows) instead of a blank rectangle. Lines use
 * `bg-muted-foreground/10` because the card surface itself is `bg-muted` —
 * the default skeleton color would be invisible on it.
 */
const SummaryCardSkeleton = ({ className }: { className?: string }) => (
  <div className={`flex h-40 flex-col gap-4 rounded-lg bg-muted p-4 ${className ?? ''}`}>
    <div className="flex items-center gap-2">
      <Skeleton className="h-8 w-8 shrink-0 rounded-md bg-muted-foreground/10" />
      <Skeleton className="h-4 w-32 bg-muted-foreground/10" />
    </div>
    <div className="flex flex-1 flex-col justify-between py-1">
      {[0, 1, 2].map(row => (
        <div key={row} className="flex items-center justify-between">
          <Skeleton className="h-3 w-16 bg-muted-foreground/10" />
          <Skeleton className="h-3 w-28 bg-muted-foreground/10" />
        </div>
      ))}
    </div>
  </div>
);

const SummarySkeleton = () => (
  <div className="flex gap-4">
    <SummaryCardSkeleton className="basis-[85%] sm:basis-[420px]" />
    <SummaryCardSkeleton className="hidden sm:flex sm:basis-[420px]" />
    <SummaryCardSkeleton className="hidden lg:flex lg:basis-[420px]" />
  </div>
);

/**
 * Builds both the expanded and compact renditions of the overheads summary so
 * the page can plug them into a `CollapsibleHeaderSection`. Both renditions
 * share the same data source, keeping the two views in sync as the page
 * scrolls. All amounts arrive already converted to the organization's default
 * currency.
 */
export const useOverheadsSummaryRenditions = ({
  filters,
}: {
  filters: FilterValues['values'];
}): OverheadsSummaryRenditions => {
  const { t } = useTranslation('authenticated.overheads');
  const { data: summary, isLoading, error, refetch, isFetching } = useOverheadsSummary({ filters });

  const IncomeIcon = getOverheadTypeIcon(OverheadType.Income);
  const CostIcon = getOverheadTypeIcon(OverheadType.Cost);

  const renderExpandedBody = () => {
    if (isLoading) return <SummarySkeleton />;
    if (error) {
      return (
        <ErrorState
          className="min-h-[160px]"
          title={t('summary.loadErrorTitle')}
          error={error}
          action={{
            label: t('summary.retry'),
            onClick: () => refetch(),
            isLoading: isFetching,
          }}
        />
      );
    }
    if (!summary) return null;

    return (
      <OverheadsSummaryCardsRow>
        <OverheadsSummaryCardSlot>
          <OverheadsSummaryAnalysisCard summary={summary} />
        </OverheadsSummaryCardSlot>
        <OverheadsSummaryCardSlot>
          <OverheadsSummaryCard
            icon={<IncomeIcon className="h-4 w-4" />}
            title={t('summary.incomeTitle')}
            infoTooltip={t('summary.incomeTooltip')}
            currencyCode={summary.currencyCode}
            rows={[
              { label: t('summary.rows.pending'), bucket: summary.incomePending },
              { label: t('summary.rows.invoiced'), bucket: summary.incomeInvoiced },
            ]}
          />
        </OverheadsSummaryCardSlot>
        <OverheadsSummaryCardSlot>
          <OverheadsSummaryCard
            icon={<CostIcon className="h-4 w-4" />}
            title={t('summary.costTitle')}
            infoTooltip={t('summary.costTooltip')}
            currencyCode={summary.currencyCode}
            rows={[
              { label: t('summary.rows.planned'), bucket: summary.costPlanned },
              { label: t('summary.rows.paid'), bucket: summary.costPaid },
            ]}
          />
        </OverheadsSummaryCardSlot>
      </OverheadsSummaryCardsRow>
    );
  };

  // Full description, no clamping — the page MEASURES this header's rendered
  // height (including the bottom padding below, which acts as the gap above
  // the cards) and derives the collapse offsets from it, so the text may wrap
  // to any number of lines on any viewport without running under the cards.
  const expandedHeader = (
    <div className="flex flex-col gap-1 pb-3">
      <h2 className="text-lg font-semibold">{t('summary.title')}</h2>
      <p className="text-sm text-muted-foreground">{t('summary.description')}</p>
    </div>
  );

  return {
    expandedHeader,
    expandedBody: renderExpandedBody(),
    // The compact strip only exists once the summary is loaded; during
    // loading/error the expanded slot shows the relevant state and there's
    // nothing to collapse into.
    collapsed:
      !isLoading && !error && summary ? <OverheadsSummaryCompact summary={summary} /> : null,
  };
};
